import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, Star } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const ProductCard = ({ product, onAddToCart, compact = false }) => {
  const [selectedVariant, setSelectedVariant] = useState(product.variants[0]);
  const [isHovered, setIsHovered] = useState(false);

  const getTotalStock = () => {
    return product.variants.reduce((total, variant) => total + variant.stock_quantity, 0);
  };

  // Stock status function removed - no longer displaying stock badges

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (selectedVariant.stock_quantity === 0) {
      toast.error('This size is out of stock');
      return;
    }

    onAddToCart(product, selectedVariant, 1);
    toast.success(`Added ${product.name} (Size ${selectedVariant.size}) to cart`);
  };

  const handleQuickSelect = (e, variant) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedVariant(variant);
  };

  // Stock status variable removed - no longer displaying stock badges

  return (
    <div 
      className={`product-card relative ${compact ? 'h-auto' : 'h-full'} fade-in fade-up`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block h-full">
        {/* Image */}
        <div className="product-image relative">
          <img
            src={product.image_urls[0]}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
            data-testid={`product-image-${product.id}`}
          />
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col space-y-2">
            {product.is_featured && (
              <span className="featured-badge" data-testid={`featured-badge-${product.id}`}>
                Featured
              </span>
            )}
          </div>

          {/* Quick Actions Overlay */}
          <div className={`absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center space-x-4 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            <Button
              variant="secondary"
              size="sm"
              className="bg-white text-gray-900 hover:bg-gray-100"
              data-testid={`quick-view-button-${product.id}`}
            >
              Quick View
            </Button>
            <Button
              onClick={handleAddToCart}
              disabled={selectedVariant.stock_quantity === 0}
              size="sm"
              className="bg-brand-purple text-white hover:bg-brand-purple-dark disabled:bg-gray-400"
              data-testid={`quick-add-button-${product.id}`}
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              {selectedVariant.stock_quantity === 0 ? 'Gone' : 'Lock It In'}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Category & Brand */}
          <div className="flex items-center justify-between mb-2">
            <span className="category-badge" data-testid={`category-badge-${product.id}`}>
              {product.category.replace('_', ' ')}
            </span>
            <span className="text-sm text-gray-500 font-medium" data-testid={`brand-${product.id}`}>
              {product.brand}
            </span>
          </div>

          {/* Product Name */}
          <h3 className="font-bold text-lg mb-2 text-gray-900 line-clamp-2" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>

          {/* Price */}
          <div className="flex items-center justify-between mb-4">
            <div className="price" data-testid={`product-price-${product.id}`}>
              <span className="price-currency">$</span>
              {product.price.toFixed(2)}
            </div>
          </div>

          {/* Size Selection (if not compact) */}
          {!compact && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Size:</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.slice(0, 4).map((variant) => (
                  <button
                    key={variant.id}
                    onClick={(e) => handleQuickSelect(e, variant)}
                    className={`size-option text-sm ${
                      selectedVariant.id === variant.id ? 'selected' : ''
                    } ${variant.stock_quantity === 0 ? 'unavailable' : ''}`}
                    disabled={variant.stock_quantity === 0}
                    data-testid={`size-option-${variant.size}-${product.id}`}
                  >
                    {variant.size}
                  </button>
                ))}
                {product.variants.length > 4 && (
                  <span className="text-sm text-gray-500 flex items-center">+{product.variants.length - 4} more</span>
                )}
              </div>
            </div>
          )}

          {/* Selected variant info - REMOVED */}

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            disabled={selectedVariant.stock_quantity === 0}
            className="w-full bg-brand-purple hover:bg-brand-purple-dark text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
            data-testid={`add-to-cart-button-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {selectedVariant.stock_quantity === 0 ? 'Gone' : 'Lock It In'}
          </Button>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;